import { StationMasterType } from '../context/MasterContextProvider';
import { WindowSizeType } from '../hooks/useWindowSize';

// SVG画像のVIEWBOX値
export const VBWidth_Overview = 1120;
export const VBHeight_Overview = 1680;

export const VBWidth_ZoomMap = 1530;
export const VBHeight_ZoomMap = 3430;

// SVG内での座標をreact-zoom-pan-pinchのpx値に変換（ViewBox値 => px）
export const translateX = (
  size: WindowSizeType, //画面サイズ
  targetX: number, //SVG内での座標
  VBWidth: number, //SVGのViewBoxの幅
  VBHeight: number, //SVGのViewBoxの高さ
  scale: number //拡大率
) => {
  // 全体の領域
  const totalLength = size._w;
  //画像外の余白領域
  const margin =
    size._w * VBHeight > VBWidth * size._h
      ? size._w - (size._h * VBWidth) / VBHeight
      : 0;
  //画像の占める領域
  const imageLength = size._w - margin;
  const target = targetX / VBWidth;

  // 右にはみ出るとき
  if ((1 - target) * imageLength * scale < totalLength / 2) {
    // console.log('X a');
    return -((totalLength - margin / 2) * scale - totalLength);
  }
  // 左にはみ出るとき
  else if (target * imageLength * scale < totalLength / 2) {
    // console.log('X b');

    return -((scale * margin) / 2);
  }
  // はみ出ないとき
  else {
    // console.log('X c');
    return -((imageLength * target + margin / 2) * scale - totalLength / 2);
  }
};

export const translateY = (
  size: WindowSizeType,
  targetY: number,
  VBWidth: number,
  VBHeight: number,
  scale: number
) => {
  const totalLength = size._h;
  const margin =
    size._w * VBHeight < VBWidth * size._h
      ? size._h - (size._w * VBHeight) / VBWidth
      : 0;
  const imageLength = size._h - margin;
  const target = targetY / VBHeight;

  // 右にはみ出るとき
  if ((1 - target) * imageLength * scale < totalLength / 2) {
    // console.log('Y a');
    return -((totalLength - margin / 2) * scale - totalLength);
  }
  // 左にはみ出るとき
  else if (target * imageLength * scale < totalLength / 2) {
    // console.log('Y b');

    return -((scale * margin) / 2);
  }
  // はみ出ないとき
  else {
    // console.log('Y c');
    return -((imageLength * target + margin / 2) * scale - totalLength / 2);
  }
};

// react-zoom-pan-pinchから取得したpxの座標をSVG内の座標値に変換（px ⇒ ViewBox値）
export const revertX = (
  x: number, //pxのx座標
  size: WindowSizeType, //画面サイズ
  VBWidth: number, //SVGのViewBoxの幅
  VBHeight: number, //SVGのViewBoxの高さ
  scale: number //拡大率
) => {
  const margin =
    size._w * VBHeight > VBWidth * size._h
      ? size._w - (size._h * VBWidth) / VBHeight
      : 0;
  const imageLength = size._w - margin;

  // 変換後の座標（割合）
  const VBXrate =
    (-x + size._w / 2 - (margin * scale) / 2) / (imageLength * scale);
  // 変換後の座標
  const VBX = VBWidth * VBXrate;

  return x > 0 ? 0 : VBX;
};

export const revertY = (
  y: number, //pxのy座標
  size: WindowSizeType, //画面サイズ
  VBWidth: number, //SVGのViewBoxの幅
  VBHeight: number, //SVGのViewBoxの高さ
  scale: number //拡大率
) => {
  const margin =
    size._w * VBHeight < VBWidth * size._h
      ? size._h - (size._w * VBHeight) / VBWidth
      : 0;
  const imageLength = size._h - margin;

  const VBYrate =
    (-y + size._h / 2 - (margin * scale) / 2) / (imageLength * scale);
  const VBY = VBHeight * VBYrate;

  return y > 0 ? 0 : VBY;
};

// 電停が画面内にちょうど収まるズーム倍率を計算
export const calcZoomScaleForWidth = (
  size: WindowSizeType,
  VBWidth: number,
  VBHeight: number,
  stationWidth: number, //電停のSVG内の横幅（確保したい左右の余白含む）
  maxScale: number //倍率の上限
) => {
  const margin =
    size._w * VBHeight > VBWidth * size._h
      ? size._w - (size._h * VBWidth) / VBHeight
      : 0;
  const imageLength = size._w - margin;
  const stationLength = (imageLength * stationWidth) / VBWidth;
  return Math.min(size._w / stationLength, maxScale);
};

export const calcZoomScaleForHeight = (
  size: WindowSizeType,
  VBWidth: number,
  VBHeight: number,
  stationWidth: number, //電停のSVG内の高さ（確保したい上下の余白含む）
  maxScale: number //倍率の上限
) => {
  const margin =
    size._w * VBHeight < VBWidth * size._h
      ? size._h - (size._w * VBHeight) / VBWidth
      : 0;
  const imageLength = size._h - margin;
  const stationLength = (imageLength * stationWidth) / VBHeight;
  return Math.min(size._h / stationLength, maxScale);
};

// 入力のSVG座標に最も近い電停の電停番号を返す
export const nearStation = (
  x: number,
  y: number,
  stations: Array<StationMasterType>,
  kind: 'overview' | 'zoom'
) => {
  let minDist = Infinity;
  let minStationNo = '';
  for (const station of stations) {
    const dist =
      kind === 'zoom'
        ? Math.sqrt((x - station.x_zoom) ** 2 + (y - station.y_zoom) ** 2)
        : Math.sqrt(
            (x - station.x_overview) ** 2 + (y - station.y_overview) ** 2
          );
    if (dist < minDist) {
      minDist = dist;
      minStationNo = station.station_no;
    }
  }

  return minStationNo;
};
